// Drawola Studio: the first-visit welcome. Fork-only.
//
// React twin of the static `#drawola-welcome` block in index.html. It keeps
// the same DOM and class names, so the handover from the static node to this
// component does not move anything on screen.
//
// The Studio renders outside the `<Excalidraw>` tree, so translation goes
// through `useWelcomeText()` here, never upstream's `useI18n()`.

import React from "react";

import { AppLanguageSelect } from "../app-language/AppLanguageSelect";
import { collaborationGuideHref } from "../manual/guides";

import { useWelcomeText } from "./i18n";

const TITLE_ID = "drawola-welcome-title";

const POINT_KEYS = [
  "studio.welcome.points.whiteboard",
  "studio.welcome.points.flowchart",
  "studio.welcome.points.collab",
  "studio.welcome.points.drive",
] as const;

type StudioWelcomeProps = {
  /** Called when the visitor presses Continue; the Studio decides what follows. */
  onContinue: () => void;
  /** Blocks the CTA while the store boots, lists or a creation is running. */
  disabled?: boolean;
};

/**
 * Landing view for a visitor who has not acknowledged the welcome yet: a
 * language picker, what DrawOla is, four short points, a Continue button and
 * a link to the guide in the language the page shows, when it has one.
 */
export const StudioWelcome: React.FC<StudioWelcomeProps> = ({
  onContinue,
  disabled = false,
}) => {
  const { t, code } = useWelcomeText();
  const guideHref = collaborationGuideHref(code);

  return (
    <section className="drawola-welcome" aria-labelledby={TITLE_ID}>
      <AppLanguageSelect
        className="drawola-welcome__language"
        label={t("studio.language")}
      />
      <h1 id={TITLE_ID} className="drawola-welcome__title">
        {t("studio.welcome.title")}
      </h1>
      <p className="drawola-welcome__lead">{t("studio.welcome.lead")}</p>
      <ul className="drawola-welcome__points">
        {POINT_KEYS.map((key) => (
          <li key={key} className="drawola-welcome__point">
            {t(key)}
          </li>
        ))}
      </ul>
      <div className="drawola-welcome__actions">
        <button
          type="button"
          className="drawola-welcome__cta"
          onClick={onContinue}
          disabled={disabled}
        >
          {t("studio.welcome.cta")}
        </button>
        {guideHref && (
          <a
            className="drawola-welcome__manual"
            href={guideHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("studio.welcome.manual")}
          </a>
        )}
      </div>
    </section>
  );
};
